import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import '../components/AllCss/ConfirmDialog.css';

const ConfirmContext = createContext(null);

let confirmApi = null;
export function setConfirmApi(api) {
  confirmApi = api;
}

/** Imperative confirm for handlers outside React hooks */
export function confirm(opts) {
  if (confirmApi) return confirmApi(opts);
  // eslint-disable-next-line no-alert -- fallback if provider not mounted
  return Promise.resolve(window.confirm(opts?.description || opts?.title || 'Continue?'));
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);

  const confirmFn = useCallback(
    ({ title, description, confirmLabel = 'Confirm', cancelLabel = 'Cancel', destructive = false }) =>
      new Promise((resolve) => {
        previousFocusRef.current = document.activeElement;
        resolveRef.current = resolve;
        setState({ title, description, confirmLabel, cancelLabel, destructive });
      }),
    []
  );

  useEffect(() => {
    setConfirmApi(confirmFn);
    return () => setConfirmApi(null);
  }, [confirmFn]);

  useEffect(() => {
    if (!state || !dialogRef.current) return undefined;
    const focusable = dialogRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    const trap = (e) => {
      if (e.key !== 'Tab' || focusable.length === 0) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [state]);

  const close = useCallback((result) => {
    setState(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
    const prev = previousFocusRef.current;
    if (prev && typeof prev.focus === 'function') {
      requestAnimationFrame(() => prev.focus());
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') close(false);
  };

  return (
    <ConfirmContext.Provider value={{ confirm: confirmFn }}>
      {children}
      {state && (
        <div
          className="confirm-dialog-overlay"
          role="presentation"
          onClick={() => close(false)}
          onKeyDown={handleKeyDown}
        >
          <div
            ref={dialogRef}
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="confirm-dialog-title" className="confirm-dialog-title">
              {state.title}
            </h2>
            {state.description && (
              <p id="confirm-dialog-desc" className="confirm-dialog-desc">
                {state.description}
              </p>
            )}
            <div className="confirm-dialog-actions">
              <button type="button" className="confirm-dialog-btn confirm-dialog-btn--secondary" onClick={() => close(false)}>
                {state.cancelLabel}
              </button>
              <button
                type="button"
                className={`confirm-dialog-btn confirm-dialog-btn--primary${state.destructive ? ' confirm-dialog-btn--destructive' : ''}`}
                onClick={() => close(true)}
                autoFocus
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.confirm;
}
